import type { PublicProfileResponse, BanScope, DerivedPermissionFlags } from '@mrb91002/shared-types'
import { getCurrentUser, AuthUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth'
import { createContext, useContext, useState, useEffect, ReactNode, Context, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchOwnSecureProfile } from '../api/fetchers/profile'
import { registerTokenRefreshCallback } from '../api/secure/helpers'

/*
 * ⚠️  CRITICAL TODO: FIX COGNITO LOGOUT CONFIGURATION ⚠️
 *
 * PROBLEM: Global logout (signOut({ global: true })) fails with:
 * "Required String parameter 'redirect_uri' is not present"
 *
 * CURRENT WORKAROUND: Using local-only logout (global: false)
 *
 * SECURITY IMPACT: Users remain logged in on other devices/browsers
 *
 * ROOT CAUSE: Mismatch between Cognito logout URL parameters
 * - Cognito expects 'redirect_uri' but receives 'logout_uri'
 * - CDK logoutUrls config vs Amplify OAuth logout implementation
 *
 * INVESTIGATION NEEDED:
 * 1. Check CDK Cognito construct logout URL configuration
 * 2. Verify Amplify OAuth logout parameter mapping
 * 3. Test with different Amplify signOut options
 * 4. Consider custom logout URL construction
 *
 * PRIORITY: HIGH - Affects user security across devices
 *
 * ERROR DETAILS:
 * URL: https://dev-mbenav-pattern-warehouse-v3.auth.us-west-2.amazoncognito.com/error
 * Params: client_id=5gh30bpinv1cefcrm4d3b09ugs&logout_uri=https%3A%2F%2Flocalhost%3A3000%2F
 * Expected: redirect_uri parameter instead of logout_uri
 */

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  error: string | null
  accessToken: string | null
  idToken: string | null
  session: Record<string, unknown> | null
  screen_name: string | null
  setScreenName: (sn: string | null) => void
  profile: PublicProfileResponse | null
  setProfile: (profile: PublicProfileResponse | null) => void
  restrictedActions: BanScope[]
  isRestricted: boolean
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getIdToken: () => Promise<string | null>
  getAccessToken: () => Promise<string | null>
  getCurrentSession: () => Promise<Record<string, unknown> | null>
  reloadProfile: () => Promise<void>
}

const AuthContext: Context<AuthContextType | undefined> = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [idToken, setIDToken] = useState<string | null>(null)
  const [session, setSession] = useState<Record<string, unknown> | null>(null)

  const [screen_name, setScreenName] = useState<string | null>(null)
  const [profile, setProfile] = useState<PublicProfileResponse | null>(null)
  // Derived ban-state flags blended from the secure profile `permissions` field.
  // Non-restricted users won't have `permissions` populated, so default to empty.
  const [restrictedActions, setRestrictedActions] = useState<BanScope[]>([])
  const isRestricted = restrictedActions.length > 0

  // Function declarations should be moved to the top
  const getCurrentSession = useCallback(async (): Promise<Record<string, unknown> | null> => {
    try {
      const session = await fetchAuthSession()
      return session as Record<string, unknown>
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      }
      return null
    }
  }, [])

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      }
      return null
    }
  }, [])

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.accessToken?.toString() || null
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      }
      return null
    }
  }, [])

  const signIn = useCallback(async (): Promise<void> => {
    try {
      console.log('🔐 [AUTH DEBUG] Starting sign in from origin:', window.location.origin)
      console.log('🔐 [AUTH DEBUG] Current URL:', window.location.href)
      await signInWithRedirect()
    } catch (error: unknown) {
      console.error('🔴 [AUTH ERROR] Sign in failed:', error)
      if (error instanceof Error) {
        console.error('🔴 [AUTH ERROR] Error message:', error.message)
        console.error('🔴 [AUTH ERROR] Error stack:', error.stack)
        setError(error.message)
      }
    }
  }, [])

  const signOut = useCallback(async (): Promise<void> => {
    console.log('🔴 [AUTH DEBUG] Manual sign out initiated')

    // Clear all local state immediately
    setIsAuthenticated(false)
    setUser(null)
    setError(null)
    setAccessToken(null)
    setIDToken(null)
    setSession(null)
    setScreenName(null)
    setProfile(null)
    setRestrictedActions([])
    localStorage.removeItem('fpw_screen_name')

    // Clear Cognito tokens from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key)
      }
    })

    try {
      // Try Amplify signOut with local only (no redirect)
      const { signOut } = await import('aws-amplify/auth')
      await signOut({ global: false })
      console.log('🔴 [AUTH DEBUG] Local signOut successful')
    } catch (error: unknown) {
      console.error('🔴 [AUTH ERROR] Local signOut failed:', error)
      // Continue anyway since we cleared local state
    }

    // Navigate to home page
    navigate('/')
  }, [navigate])

  useEffect(() => {
    registerTokenRefreshCallback((token) => setIDToken(token))
  }, [])

  // On mount: load user and profile
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔐 [AUTH DEBUG] Starting auth initialization...')

        // First check if we have valid tokens
        const session = await fetchAuthSession()
        console.log('🔐 [AUTH DEBUG] Session check:', {
          hasTokens: !!session.tokens,
          hasIdToken: !!session.tokens?.idToken,
          hasAccessToken: !!session.tokens?.accessToken,
        })

        if (!session.tokens?.idToken || !session.tokens?.accessToken) {
          console.log('🔴 [AUTH DEBUG] No valid tokens found - user not authenticated')
          throw new Error('No valid tokens')
        }

        const currentUser = await getCurrentUser()
        console.log('🔐 [AUTH DEBUG] Current user object:', currentUser)
        console.log('🔐 [AUTH DEBUG] User ID for manual lookup:', currentUser.userId)

        // Validate that we have a proper user with userId
        if (!currentUser.userId) {
          console.log('🔴 [AUTH DEBUG] Invalid user object - no userId')
          throw new Error('Invalid user - no userId')
        }

        console.log('🔐 [AUTH DEBUG] Setting user and authenticated state')
        setUser(currentUser)
        setIsAuthenticated(true)
        setError(null)

        // Skip profile fetching on create profile page
        if (window.location.pathname === '/create/profile') {
          console.log('🔐 [AUTH DEBUG] On create profile page, but checking if profile exists...')
          // Don't skip - let's check if user actually has a profile
        }

        // Skip profile fetching on login redirect page
        if (window.location.pathname === '/login-redirect') {
          console.log('🔐 [AUTH DEBUG] On login redirect page, skipping profile fetch')
          return
        }

        // Try to get screen name from localStorage first (fallback for CORS issues)
        const cachedScreenName = localStorage.getItem('fpw_screen_name')
        if (cachedScreenName) {
          console.log('🔐 [AUTH DEBUG] Using cached screen name:', cachedScreenName)
          setScreenName(cachedScreenName)
        }

        // Try to fetch profile
        let loadedProfile: PublicProfileResponse | null = null
        try {
          if (currentUser.userId) {
            console.log('🔐 [AUTH DEBUG] Fetching own secure profile (self-mode) for user ID:', currentUser.userId)
            // Use the idToken from the session already fetched at the top of initAuth.
            const idToken = session.tokens?.idToken?.toString() || ''
            const secureResponse = await fetchOwnSecureProfile(idToken)
            loadedProfile = secureResponse.profile
            // Blend derived ban-state flags into auth state. Non-restricted users won't
            // have `permissions` populated (the field is optional), so default to empty.
            const permissions: DerivedPermissionFlags = secureResponse.permissions ?? {
              restricted_actions: [],
              is_restricted: false,
            }
            setRestrictedActions(permissions.restricted_actions)
            setProfile(loadedProfile)
            if (loadedProfile?.screen_name) {
              setScreenName(loadedProfile.screen_name)
              // Cache screen name for future use
              localStorage.setItem('fpw_screen_name', loadedProfile.screen_name)
              console.log('🔐 [AUTH DEBUG] Loaded and cached profile:', loadedProfile)
            } else {
              // Authenticated user exists but has no profile - redirect to create profile
              console.log('🔄 [AUTH] Authenticated user without profile, redirecting to create profile')
              setProfile(null)
              setScreenName(null)
              setRestrictedActions([])
              if (window.location.pathname !== '/create/profile' && window.location.pathname !== '/login-redirect') {
                navigate('/create/profile')
              }
            }
          }
        } catch (err: unknown) {
          console.error('🔴 [AUTH ERROR] Profile fetch failed:', err)
          const errorStatus =
            err && typeof err === 'object' && 'status' in err ? (err as { status?: number }).status : undefined
          console.log('🔐 [AUTH DEBUG] Error status:', errorStatus)

          setProfile(null)
          setRestrictedActions([])

          // 404 means profile doesn't exist (self-mode returns 404 when the user has not
          // created a profile yet) - redirect to create profile. secureFetch attaches the
          // HTTP status to the thrown error, so this detects the no-profile case the same
          // way the previous public-by-id path did.
          if (errorStatus === 404) {
            console.log('🔄 [AUTH] Profile not found (404), redirecting to create profile')
            setScreenName(null)
            localStorage.removeItem('fpw_screen_name')
            if (window.location.pathname !== '/create/profile' && window.location.pathname !== '/login-redirect') {
              navigate('/create/profile')
            }
          } else {
            // Network error or other issue - keep cached screen name if available
            console.log('🔐 [AUTH DEBUG] Using cached screen name if available:', cachedScreenName)
            if (cachedScreenName) {
              setScreenName(cachedScreenName)
              console.log('🔐 [AUTH DEBUG] Keeping cached screen name during profile fetch failure')
            } else {
              setScreenName(null)
              console.log('🔐 [AUTH DEBUG] No cached screen name and profile fetch failed - staying on current page')
            }
          }
        }
      } catch (error: unknown) {
        console.log('🔴 [AUTH DEBUG] Auth initialization failed:', error)

        // Check if this is a token expiration vs not logged in
        if (
          error instanceof Error &&
          (error.message.includes('token') ||
            error.message.includes('expired') ||
            error.message.includes('NotAuthorizedException'))
        ) {
          console.log(
            '🔴 [AUTH DEBUG] Token expired/invalid - clearing auth state but preserving screen name temporarily',
          )
          // Clear corrupted Cognito tokens but preserve screen name for better UX
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
              localStorage.removeItem(key)
            }
          })
          // Keep cached screen name so user sees familiar state before re-login
          // It will be cleared on successful re-authentication or manual logout
        } else {
          console.log('🔴 [AUTH DEBUG] User not authenticated - clearing all cached data')
          // Clear everything for genuine "not logged in" state
          localStorage.removeItem('fpw_screen_name')
        }

        setIsAuthenticated(false)
        setUser(null)
        setProfile(null)
        setRestrictedActions([])
        // Keep screen name from cache if token expired (better UX)
        const cachedName = localStorage.getItem('fpw_screen_name')
        setScreenName(cachedName)

        if (error instanceof Error) {
          setError(error.message)
        }
      } finally {
        console.log('🔐 [AUTH DEBUG] Auth initialization complete, isLoading -> false')
        setIsLoading(false)
      }
    }

    initAuth()
  }, [getCurrentSession, navigate])

  useEffect(() => {
    const fetchSessionData = async (): Promise<void> => {
      const token = await fetchAuthSession()
      const sess = await getCurrentSession()

      setIDToken(token.tokens?.idToken?.toString() || null)
      setAccessToken(token.tokens?.accessToken?.toString() || null)
      setSession(sess)
    }

    if (isAuthenticated) {
      fetchSessionData()
    }
  }, [isAuthenticated, getCurrentSession]) // Add getCurrentSession to the dependency array

  const reloadProfile = useCallback(async () => {
    if (!user) return
    try {
      const idToken = (await getIdToken()) || ''
      const secureResponse = await fetchOwnSecureProfile(idToken)
      const loadedProfile = secureResponse.profile
      setProfile(loadedProfile)
      setScreenName(loadedProfile?.screen_name || null)
      const permissions: DerivedPermissionFlags = secureResponse.permissions ?? {
        restricted_actions: [],
        is_restricted: false,
      }
      setRestrictedActions(permissions.restricted_actions)
    } catch {
      setProfile(null)
      setScreenName(null)
      setRestrictedActions([])
    }
  }, [user, getIdToken])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        error,
        accessToken,
        idToken,
        session,
        screen_name,
        setScreenName,
        profile,
        setProfile,
        restrictedActions,
        isRestricted,
        signIn,
        signOut,
        getIdToken,
        getAccessToken,
        getCurrentSession,
        reloadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
